import moment from 'moment';
import {
  IResourceDetailList,
  IResourceList,
} from 'src/components/challenge/ChallengeInterfaces';
import {
  ChallengeTypeEnum,
  ResourceTypeDetailEnum,
  ResourceTypeEnum,
  UserResourceEnum,
} from 'src/shared/enums';
import {
  IChallengeDates,
  IChallengeEvent,
  IChallengeQuiz,
  IQuizAnswer,
  IQuizQuestion,
  isVideoValidFormat,
} from 'src/shared/models';
import { getResourceDetail } from 'utils';

const { PARTICIPATION, QUESTION, ANSWER } = ResourceTypeEnum;

const {
  RETRY,
  CONTINUE,
  SHOW_STATISTICS,
  PRE_PARTICIPATION_TEXT,
  CORRECT,
  QUIZ_CONTENT,
  QUIZ_VIDEO_URL,
  QUESTION_IMAGE_URL,
  QUESTION_JUSTIFICATION,
  QUIZ_IMAGE,
  QUIZ_POINTS_LABEL,
} = ResourceTypeDetailEnum;

export const buildChallengeQuiz = (
  quiz: IResourceList,
  idChallenge: number,
  challengeType: ChallengeTypeEnum,
  mainEvent: IChallengeEvent,
  challengeDates: IChallengeDates
): IChallengeQuiz => {
  let challengeQuiz: IChallengeQuiz;
  const questions: IQuizQuestion[] = [];
  let accPoints = 0;
  let idResource: number = quiz.idResource;
  let userStatus: UserResourceEnum = UserResourceEnum.INACTIVE;
  let dateDone: moment.Moment;
  const disabled = false;
  const quizLabel = getResourceDetail(quiz, QUIZ_POINTS_LABEL);

  const hasImage = false;
  let completationTitle = 'QuizBuilder-completation-title-not-defined';

  switch (challengeType) {
    case ChallengeTypeEnum.TALK:
      completationTitle = 'page.challenge.congratulations-quiz-completed';
      break;
    case ChallengeTypeEnum.FORMATION:
      completationTitle = 'page.challenge.congratulations-training-completed';
      break;
  }

  if (quiz.resourceDetailList?.length) {
    quiz.resourceDetailList
      .filter((_detail) => _detail.status)
      .forEach((_detail) => {
        switch (_detail.idResourceTypeD.idResourceTypeD) {
          case RETRY:
            challengeQuiz = {
              ...challengeQuiz,
              allowRetry: _detail.value === 'true' ? true : false,
            };
            break;
          case CONTINUE:
            challengeQuiz = {
              ...challengeQuiz,
              allowContinue: _detail.value === 'true' ? true : false,
            };
            break;
          case SHOW_STATISTICS:
            challengeQuiz = {
              ...challengeQuiz,
              showStatistics: _detail.value === 'true' ? true : false,
            };
            break;
          case PRE_PARTICIPATION_TEXT:
            challengeQuiz = {
              ...challengeQuiz,
              preInscriptionText: _detail.value,
            };
            break;
          case QUIZ_CONTENT:
            challengeQuiz = {
              ...challengeQuiz,
              description: _detail.value,
            };
            break;
          case QUIZ_IMAGE:
            challengeQuiz = {
              ...challengeQuiz,
              urlImage: _detail.value,
            };
            break;
        }
      });
  }

  quiz.resourceList
    ?.filter((_detail) => _detail.status)
    .forEach((resource: IResourceList, index: number, elements) => {
      const isLastQuestion = index == elements.length - 1;

      switch (resource.idResourceType.idResourceType) {
        case PARTICIPATION:
          accPoints += !resource.score ? 0 : resource.score.points;
          idResource = resource.idResource;
          const usersM2MList = resource.usersM2MList
            .filter((item) => item.status)
            .pop();

          if (Boolean(usersM2MList)) {
            userStatus = usersM2MList.statusResource as UserResourceEnum;
            dateDone = moment(usersM2MList.createDate);
          }

          break;
        case QUESTION:
          const { points, question } = buildQuizQuestion(
            resource,
            isLastQuestion
          );
          questions.push(question);
          accPoints += points;
          break;
      }
    });

  challengeQuiz = {
    ...challengeQuiz,
    completationTitle,
    disabled,
    idChallenge,
    isCompleted: Boolean(dateDone),
    isQuizDone: Boolean(dateDone),
    shouldRender: getShouldRenderQuiz(challengeType, mainEvent, {
      isQuizDone: Boolean(dateDone),
      challengeQuiz,
      challengeDates,
    }),
    showPresentation: challengeType === ChallengeTypeEnum.TALK,
    title: quiz.name,
    icon: 'trainning',
    quizLabel: quizLabel?.value,
  };

  return {
    ...challengeQuiz,
    dateDone,
    hasImage,
    idResource,
    points: Math.max(accPoints, 0),
    questions,
    totalResponses: 0,
    userStatus,
  };
};

const getShouldRenderQuiz = (
  challengeType: ChallengeTypeEnum,
  mainEvent: IChallengeEvent,
  data: any
): boolean => {
  let shouldRenderQuiz = false;

  switch (challengeType) {
    case ChallengeTypeEnum.TALK:
      if (Boolean(mainEvent)) {
        const { attend, status } = mainEvent;
        shouldRenderQuiz = attend.isPerformed && status.isExpired;
      }
      break;

    case ChallengeTypeEnum.FORMATION:
      const {
        isQuizDone,
        challengeQuiz: { allowRetry },
        challengeDates: { startDate, endDate },
      } = data;
      const isChallengeInLife = isBetweenDate(startDate, endDate);

      const challengeExpired = moment().isAfter(endDate);
      const isAvailableToDo = !isQuizDone && isChallengeInLife;
      const isAvailableToRepeat = allowRetry && challengeExpired;
      const isRepeatable = isQuizDone && allowRetry && isChallengeInLife;

      switch (true) {
        case isAvailableToDo:
        case isRepeatable:
        case isAvailableToRepeat:
          shouldRenderQuiz = true;
          break;
      }
      break;
  }

  return shouldRenderQuiz;
};

const buildQuizQuestion = (
  questionData: IResourceList,
  isLastQuestion: boolean
): { points: number; question: IQuizQuestion } => {
  const { name, idResource, indResource, i18nList } = questionData;
  const answers: IQuizAnswer[] = [];
  let urlVideo: string;
  let urlImage: string;
  let score = 0;
  let textJustification: string;
  let hasCorrectAnswer = false;

  questionData.resourceDetailList
    .filter((_detail) => _detail.status)
    .forEach((resource: IResourceDetailList) => {
      switch (resource.idResourceTypeD.idResourceTypeD) {
        case QUIZ_VIDEO_URL:
          urlVideo = resource.value;
          break;
        case QUESTION_IMAGE_URL:
          urlImage = resource.value;
          break;
        case QUESTION_JUSTIFICATION:
          textJustification = resource.value;
          break;
      }
    });

  questionData.resourceList
    .filter((_detail) => _detail.status)
    .forEach((answerData: IResourceList) => {
      if (ANSWER == answerData.idResourceType.idResourceType) {
        const { points, answer } = buildQuizAnswer(answerData);
        hasCorrectAnswer = hasCorrectAnswer || answer.isCorrect;
        answers.push(answer);
        score = Math.max(score, points);
      }
    });

  const format = Boolean(urlVideo) ? urlVideo.split('.') : '';
  const isFromFile = isVideoValidFormat(format[format?.length - 1]);

  return {
    points: score,
    question: {
      idResource,
      indResource,

      answers,
      i18nList,
      score,
      title: name,

      hasAnswers: answers.length > 0,
      hasCorrectAnswer,
      hasImage: Boolean(urlImage),
      hasJustification: Boolean(textJustification),
      hasVideo: Boolean(urlVideo),
      isLastQuestion,
      isVideoFromFile: Boolean(urlVideo) && isFromFile,

      textJustification,
      urlImage,
      urlVideo,
    },
  };
};

const buildQuizAnswer = (
  answerData: IResourceList
): { points: number; answer: IQuizAnswer } => {
  let isCorrect = false;

  const { indResource, idResource, name, i18nList, resourceDetailList, score } =
    answerData;

  resourceDetailList
    .filter((_detail) => _detail.status)
    .forEach((detail: IResourceDetailList) => {
      if (CORRECT == detail.idResourceTypeD.idResourceTypeD) {
        isCorrect = detail.value === 'true';
      }
    });

  const points = !score ? 0 : score.points;

  return {
    points,
    answer: {
      indResource,
      idResource,
      name,
      i18nList,
      isCorrect,
      userResponses: 0,
      score: points,
    },
  };
};

// TODO: remove it and use "isAttendAvailable"
const isBetweenDate = (
  initial: Date | string | moment.Moment,
  last: Date | string | moment.Moment
) => {
  return moment().isBetween(initial, last);
};
